import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'react-day-picker/dist/style.css';

import Login from './pages/Login';
import MainLayout from './components/layout/MainLayout';
import { Toaster } from './components/ui/toaster';

import Broadcast from '@/pages/Meta/Broadcast/Broadcast.tsx';
import BroadcastEditorPage from '@/pages/Meta/Broadcast/BroadcastEditorPage.tsx';
import Connection from '@/pages/Meta/Connection/Connection.tsx';
import MetaCallback from '@/pages/Meta/Connection/MetaCallback.tsx';
import PageDetails from '@/pages/Meta/Connection/PageDetails.tsx';
import Funnels from '@/pages/Meta/Flow/Funnel/Funnels.tsx';
import FlowBuilder from '@/pages/Meta/Flow/Builder/Builder.tsx';
import Leads from '@/pages/Meta/Leads/Leads.tsx';
import CommentAutoReplies from '@/pages/Meta/CommentAutoReplies/CommentAutoReplies.tsx';
import CommentAutoReplyBuilder from '@/pages/Meta/CommentAutoReplies/Builder/CommentAutoReplyBuilder.tsx';
import ChatfoodSync from '@/pages/Meta/ChatfoodSync/ChatfoodSync.tsx';

import AiKeys from '@/pages/Meta/Settings/AiKeys.tsx';
import AiTokenUsage from '@/pages/Meta/Settings/AiTokenUsage.tsx';
import ClickReports from '@/pages/Meta/Reports/ClickReports.tsx';
import IceBreakers from '@/pages/Meta/IceBreakers/IceBreakers.tsx';
import Esteira from '@/pages/Meta/Esteira/Esteira.tsx';
import EsteiraGroupPage from '@/pages/Meta/Esteira/EsteiraGroupPage.tsx';
import ErrorAnalysis from '@/pages/Meta/ErrorAnalysis/ErrorAnalysis.tsx';
import TemplateLibraryPage from '@/pages/Meta/TemplateLibrary/TemplateLibraryPage.tsx';
import TemplateBuilderPage from '@/pages/Meta/TemplateBuilder/TemplateBuilderPage.tsx';
import BuilderWizardPage from '@/pages/Meta/TemplateBuilder/BuilderWizardPage.tsx';
import BuilderItemPage from '@/pages/Meta/TemplateBuilder/BuilderItemPage.tsx';
import Dashboard from '@/pages/Meta/Dashboard/Dashboard.tsx';
import SocialPublisher from '@/pages/Meta/SocialPublisher/SocialPublisher.tsx';
import SocialPublisherFeeds from '@/pages/Meta/SocialPublisher/FeedsPage.tsx';
import SocialPublisherAssets from '@/pages/Meta/SocialPublisher/AssetsPage.tsx';
import SocialPublisherDetail from '@/pages/Meta/SocialPublisher/PageConfigDetail.tsx';
import SocialPublisherCategoryDetail from '@/pages/Meta/SocialPublisher/CategoryConfigDetail.tsx';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5,
            gcTime: 1000 * 60 * 60 * 24,
        },
    },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const token = localStorage.getItem('access_token');
    if (!token) {
        return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/callback" element={<MetaCallback />} />
                    <Route path="/connect/meta/callback" element={<MetaCallback />} />

                    <Route
                        element={
                            <ProtectedRoute>
                                <MainLayout />
                            </ProtectedRoute>
                        }
                    >
                        {/* Meta */}
                        <Route path="/meta/broadcasts" element={<Broadcast />} />
                        <Route path="/meta/broadcasts/page/:pageId" element={<BroadcastEditorPage />} />
                        <Route path="/meta/connection" element={<Connection />} />
                        <Route path="/meta/connection/pages/:id" element={<PageDetails />} />
                        <Route path="/meta/flow" element={<Funnels />} />
                        <Route path="/meta/flow/:funnelId" element={<FlowBuilder />} />
                        <Route path="/meta/leads" element={<Leads />} />
                        <Route path="/meta/comment-auto-replies" element={<CommentAutoReplies />} />
                        <Route path="/meta/comment-auto-replies/:ruleId" element={<CommentAutoReplyBuilder />} />
                        <Route path="/meta/sync" element={<ChatfoodSync />} />

                        {/* Meta Settings (chaves de IA, consumo de tokens) */}
                        <Route path="/meta/settings/ai-keys" element={<AiKeys />} />
                        <Route path="/meta/settings/ai-token-usage" element={<AiTokenUsage />} />

                        {/* Reports */}
                        <Route path="/meta/reports/clicks" element={<ClickReports />} />
                        <Route path="/meta/error-analysis" element={<ErrorAnalysis />} />

                        {/* Ice Breakers */}
                        <Route path="/meta/ice-breakers" element={<IceBreakers />} />

                        {/* Esteira (visível apenas pra user_id=1 enquanto está em beta) */}
                        <Route path="/meta/esteira" element={<Esteira />} />
                        <Route path="/meta/esteira/:groupId" element={<EsteiraGroupPage />} />

                        {/* Biblioteca de modelos (gate user_id=1 também) */}
                        <Route path="/meta/template-library" element={<TemplateLibraryPage />} />
                        {/* Sem item na sidebar de propósito — acesso só por URL até validar */}
                        <Route path="/meta/template-builder" element={<TemplateBuilderPage />} />
                        <Route path="/meta/template-builder/new" element={<BuilderWizardPage />} />
                        <Route path="/meta/template-builder/:id" element={<BuilderItemPage />} />

                        {/* Dashboard de Relatórios (novo módulo) */}
                        <Route path="/meta/dashboard" element={<Dashboard />} />

                        {/* Social Publisher (RSS → IA → Facebook auto) */}
                        <Route path="/meta/social-publisher" element={<SocialPublisher />} />
                        <Route path="/meta/social-publisher/feeds" element={<SocialPublisherFeeds />} />
                        <Route path="/meta/social-publisher/assets" element={<SocialPublisherAssets />} />
                        <Route path="/meta/social-publisher/configs/:id" element={<SocialPublisherDetail />} />
                        <Route path="/meta/social-publisher/categorias/:id" element={<SocialPublisherCategoryDetail />} />
                    </Route>

                    {/* Landing default = Dashboard. Quando o user não tem
                        `dashboard_view`, o próprio Dashboard.tsx redireciona
                        pra /meta/flow (gate granular). */}
                    <Route path="/" element={<Navigate to="/meta/dashboard" replace />} />
                </Routes>
                <Toaster />
            </BrowserRouter>
        </QueryClientProvider>
    );
}

export default App;
